'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { MarketplaceListing } from '@/lib/marketplace/types';
import { cn } from '@/lib/utils';
import { Star, Download, CheckCircle2, Sparkles } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

interface ListingCardProps {
  listing: MarketplaceListing;
  className?: string;
}

const TYPE_LABELS: Record<string, string> = {
  agent_template: 'Agent',
  connector: 'Connector',
  skill: 'Skill',
  workflow: 'Workflow',
};

export function ListingCard({ listing, className }: ListingCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate({ to: '/marketplace/$slug', params: { slug: listing.slug } });
  };

  return (
    <Card
      className={cn(
        'cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group overflow-hidden',
        className
      )}
      onClick={handleClick}
    >
      {/* Banner/Icon */}
      <div className="h-24 bg-gradient-to-br from-primary/20 to-primary/5 relative flex items-center justify-center">
        {listing.icon_url ? (
          <img
            src={listing.icon_url}
            alt={listing.name}
            className="size-16 rounded-lg object-cover"
          />
        ) : (
          <div className="size-16 rounded-lg bg-background flex items-center justify-center text-2xl font-bold text-primary">
            {listing.name.charAt(0)}
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 right-2 flex gap-1">
          {listing.publisher_verified && (
            <Badge variant="default" className="gap-1 text-xs">
              <CheckCircle2 className="size-3" />
              Verified
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="pt-4">
        {/* Type badge */}
        <Badge variant="secondary" className="text-xs mb-2">
          {TYPE_LABELS[listing.listing_type] || listing.listing_type}
        </Badge>

        {/* Name */}
        <h3 className="font-semibold line-clamp-1 group-hover:text-primary transition-colors">
          {listing.name}
        </h3>

        {/* Publisher */}
        <p className="text-sm text-muted-foreground line-clamp-1">
          by {listing.publisher_name || 'Unknown'}
        </p>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mt-2 min-h-[2.5rem]">
          {listing.short_description || 'No description'}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center gap-3">
            {/* Rating */}
            <div className="flex items-center gap-1 text-sm">
              <Star className="size-4 text-yellow-500 fill-yellow-500" />
              <span>{listing.rating_average.toFixed(1)}</span>
              <span className="text-muted-foreground">({listing.rating_count})</span>
            </div>

            {/* Downloads */}
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Download className="size-4" />
              <span>{listing.install_count.toLocaleString()}</span>
            </div>
          </div>

          {/* Price */}
          <Badge
            variant={listing.price_type === 'free' ? 'outline' : 'default'}
            className={cn(
              listing.price_type === 'free' && 'text-green-600 border-green-600'
            )}
          >
            {listing.price_type === 'free'
              ? 'Free'
              : listing.price_type === 'freemium'
              ? 'Freemium'
              : `$${(listing.price_cents / 100).toFixed(2)}`}
          </Badge>
        </div>

        {/* Tags */}
        {listing.tags && listing.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {listing.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
